/*
 *                     NOTICE
 *            COPYRIGHT 2024 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { AbPopup } from "abreact/incubator/ui/popup/AbPopup";
import { PopupUtils } from "abreact/incubator/ui/popup/PopupUtils";
import { SelectionMode } from "abreact/incubator/ui/SharedTypes";
import { AbList } from "abreact/input/AbList";
import { AbMarkdownEditor } from "abreact/input/AbMarkdownEditor";
import { AbMarkdownExtensions } from "abreact/input/markdown/AbMarkdownExtensions";
import React from "react";
/**
 * The component that renders autocomplete dialogs for a person in the AbMarkdownEditor
 *
 * @deprecated Replace {@link AbMarkdownEditor} with {@link AbMarkdownLexical} markdown editor and its child component {@link PersonPlugin}
 */
export const AbMarkdownPersonAutocomplete = props => {
  const [showPopup, setShowPopup] = React.useState(false);
  const [selection, setSelection] = React.useState(undefined);
  const [selectionIndex, setSelectionIndex] = React.useState(0);
  const [persons, setPersons] = React.useState([]); // currently available persons
  const [nameSoFar, setNameSoFar] = React.useState(undefined); // what the user has typed in the editor

  const spanRef = React.useRef(null);
  const extension = AbMarkdownExtensions.markdownPersonExtension.get();
  const {
    containingEditor,
    blockKey,
    start,
    end,
    onSelectionChange
  } = props;
  React.useEffect(() => {
    setShowPopup(true);
    if (!containingEditor.state.autocompleteMode) {
      containingEditor.setState({
        autocompleteMode: true
      });
    }
  }, [containingEditor]);
  const noPersonMatches = React.useMemo(() => {
    return {
      id: null,
      // pick a non-string value so extensions don't override
      displayName: AbReactResources.markdownEditor_noPersonMatches()
    };
  }, []);
  React.useEffect(() => {
    const contentState = containingEditor.state.editorState.getCurrentContent();
    const currentBlock = contentState.getBlockForKey(blockKey);
    const text = currentBlock.getText().substring(start + 1, end - 1);
    if (nameSoFar !== text) {
      setNameSoFar(text);
      // Make rpc to get persons. When rpc returns, set the persons into the state.
      extension.getMatchingPersons(text).then(newPersons => {
        if (newPersons.length === 0) {
          // no one matches, use the special
          newPersons.push(noPersonMatches);
        }
        setPersons(newPersons);

        // New set of matching persons: reset the selection to the first one
        setSelectionIndex(0);
        setSelection(newPersons[0]);
      });
    }
  }, [extension, nameSoFar, noPersonMatches, containingEditor, blockKey, start, end]);
  let getStartEnd = React.useCallback(() => {
    const contentState = containingEditor.state.editorState.getCurrentContent();
    const currentBlock = contentState.getBlockForKey(blockKey);
    let start = -1;
    let end = -1;
    AbMarkdownEditor.findAutocompleteEntities(currentBlock, (x, y) => {
      start = x;
      end = y;
    }, contentState);
    return {
      start,
      end
    };
  }, [containingEditor, blockKey]);

  // Helper function to inform AbMarkdownEditor that a new person was chosen.
  // When the AbMarkdownEditor issues a AUTOCOMPLETE_ENTER command, this is the person chosen.
  const changeEditorPerson = React.useCallback(person => {
    // if selection is the special, tell the caller no one is selected
    if (person?.id === noPersonMatches.id) person = undefined;
    onSelectionChange(person);
  }, [noPersonMatches, onSelectionChange]);
  React.useImperativeHandle(props.abRef, () => ({
    incrementSelection: increment => {
      const newIndex = selectionIndex + increment;
      const filteredList = persons;
      if (newIndex < 0 || newIndex >= filteredList.length) return; // can't increment past the end of the filter
      setSelectionIndex(newIndex); // set our state
      setSelection(filteredList[newIndex]); // keep selection and selectionIndex in sync
      changeEditorPerson(filteredList[newIndex]);
    },
    keyEvent: e => {
      let {
        start,
        end
      } = getStartEnd();
      if (start < 0 || end < 0) return undefined; // no autocomplete entity: let everything through
      const selectionState = props.containingEditor.state.editorState.getSelection();

      // see if cursor is still on the autocomplete entity.  If not, do nothing.
      if (e.code === "ArrowLeft" && selectionState.getAnchorOffset() <= start + 1) {
        return "handled";
      }
      if (e.code === "ArrowRight" && selectionState.getAnchorOffset() >= end - 1) {
        return "handled";
      }
      return undefined; // let arrow key pass
    },
    keyCommand: command => {
      let {
        start,
        end
      } = getStartEnd();
      if (start < 0 || end < 0) return command; // no autocomplete entity: let everything through
      const selectionState = props.containingEditor.state.editorState.getSelection();
      if (command === "delete" && selectionState.getAnchorOffset() < end - 1) {
        return "delete";
      }
      if (command === "backspace" && selectionState.getAnchorOffset() > start + 1) {
        return "backspace";
      }
      return "handled"; // by default, do nothing
    }
  }), [props, persons, selectionIndex, changeEditorPerson, getStartEnd]);
  React.useEffect(() => {
    let items = persons;
    let newSelection;
    if (items.length > 0) {
      newSelection = items[0];
      setSelectionIndex(0);
    } else {
      newSelection = undefined;
    }
    if (newSelection !== selection) {
      setSelection(newSelection); // keep selection and selectionIndex in sync
      changeEditorPerson(newSelection);
    }
  }, [persons, onSelectionChange, changeEditorPerson, selection]); // do not depend on selection: changing selection shouldn't reset selectionIndex to 0.

  const mySetSelection = person => {
    let index = persons.findIndex(x => x === person);
    setSelectionIndex(index); // keep state in sync: both selectionIndex and selection
    setSelection(person);
  };
  const choosePerson = person => {
    if (props.containingEditor) {
      // choosing the special is the same as not choosing.
      if (person?.id === noPersonMatches.id) {
        person = undefined;
      }
      props.containingEditor.exitAutocomplete(person, props.containingEditor.state.editorState); // tell editor the autocomplete chose this person
    }
  };
  const onBackgroundClick = event => {
    if (props.containingEditor) {
      props.containingEditor.exitAutocomplete(undefined, props.containingEditor.state.editorState); // tell editor the autocomplete chose no person
    } else {
      throw new Error("internal error: no markdown editor for autocomplete");
    }
  };
  const itemRenderer = person => {
    if (person.id === noPersonMatches.id) {
      // the no-person-matches person has a special renderer
      return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("span", {
        className: "choice-no-person-matches ab-italic"
      }, person.displayName));
    }
    return extension.renderChoice(person, nameSoFar) ??
    /*#__PURE__*/
    // The extension gets preference as renderer, but we have a backup.
    React.createElement("div", null, /*#__PURE__*/React.createElement("span", {
      className: "choice-display-name"
    }, person.displayName || person.id), /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement("span", {
      className: "choice-id"
    }, person.displayName ? person.id : null));
  };
  return /*#__PURE__*/React.createElement("span", {
    className: "ab-markdown-person-autocomplete",
    ref: spanRef
  }, spanRef && persons.length > 0 && /*#__PURE__*/React.createElement(AbPopup, {
    className: "ab-markdown-person-popup",
    origin: spanRef.current ?? undefined,
    positioner: PopupUtils.VERTICAL_FIXED_SIZE_POSITIONER,
    flushContent: true,
    overlay: false,
    onClose: () => setShowPopup(false),
    onBackgroundCaptureMouseDown: onBackgroundClick
  }, /*#__PURE__*/React.createElement(AbList, {
    items: persons,
    selectionMode: SelectionMode.SINGLE,
    itemRenderer: itemRenderer,
    onFocusChange: choosePerson,
    scrollToIndex: selectionIndex,
    value: selection,
    onChange: mySetSelection
  })), props.children);
};
//# sourceMappingURL=AbMarkdownPersonAutocomplete.js.map
